import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Modal from '../common/Modal';
import { useNotification } from '../../contexts/NotificationContext';
import {
  mediaBatchService,
  buildImageUrl,
  MediaBatchImage,
  MediaBatchDetail,
  AIOptions,
} from '../../services/api';
import { Button, Select, Textarea, Field, Label, Spinner } from '../ui';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  batchId: number;
  image: MediaBatchImage | null;
  onDone: (batch: MediaBatchDetail) => void;
}

const Preview = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1.1rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Shot = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;

  .cap {
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.text.secondary};
    padding: 0.4rem 0.6rem;
    background: ${({ theme }) => theme.colors.surface};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  img {
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    display: block;
  }
`;

const Empty = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.6rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.85rem;
  background: ${({ theme }) => theme.colors.surface};
`;

const Presets = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Preset = styled.button<{ $active: boolean }>`
  text-align: left;
  padding: 0.7rem 0.85rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1.5px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primarySoft : theme.colors.card)};
  color: ${({ theme }) => theme.colors.foreground};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
`;

const EngineRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const EngineBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.55rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1.5px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primarySoft : theme.colors.card)};
  color: ${({ theme }) => theme.colors.foreground};
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;

  small {
    display: block;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 0.72rem;
  }
`;

const Disclaimer = styled.p`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.warning};
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 0.6rem 0.8rem;
  margin-bottom: 1rem;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: 1.1rem;
`;

const Loading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
  padding: 1.5rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
`;

const ENGINE_LABELS: Record<string, { name: string; hint: string }> = {
  nano: { name: 'Nano Banana Pro', hint: 'Google · recomendado' },
  openai: { name: 'OpenAI GPT Image', hint: 'OpenAI' },
};

const AIEditModal: React.FC<Props> = ({ isOpen, onClose, batchId, image, onDone }) => {
  const { showNotification } = useNotification();
  const [options, setOptions] = useState<AIOptions | null>(null);
  const [action, setAction] = useState('remover_objetos');
  const [engine, setEngine] = useState('nano');
  const [style, setStyle] = useState('moderno');
  const [extra, setExtra] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setResultUrl(null);
      setToken(null);
      setExtra('');
      setAction('remover_objetos');
      mediaBatchService.aiOptions().then(setOptions).catch(() => undefined);
    }
  }, [isOpen]);

  const current = options?.actions.find((a) => a.key === action);
  const needsStyle = current?.needs_style;

  const handleGenerate = async () => {
    if (!image) return;
    try {
      setLoading(true);
      const preview = await mediaBatchService.aiEdit(batchId, image.id, {
        action,
        engine,
        style: needsStyle ? style : undefined,
        extra: extra.trim() || undefined,
      });
      setToken(preview.token);
      setResultUrl(buildImageUrl(preview.preview_url) + `?t=${Date.now()}`);
    } catch (error: any) {
      showNotification(
        error?.response?.data?.detail || 'Falha ao editar a imagem com IA',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (mode: 'new' | 'replace') => {
    if (!image || !token) return;
    try {
      setSaving(true);
      const batch = await mediaBatchService.aiSave(batchId, image.id, { token, mode });
      onDone(batch);
      showNotification(
        mode === 'replace' ? 'Imagem original substituída' : 'Nova imagem salva no lote',
        'success'
      );
      onClose();
    } catch (error: any) {
      showNotification(error?.response?.data?.detail || 'Falha ao salvar a imagem', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Editar foto com IA" size="lg">
      <Preview>
        <Shot>
          <div className="cap">Original</div>
          {image ? <img src={buildImageUrl(image.url)} alt="Original" /> : <Empty>—</Empty>}
        </Shot>
        <Shot>
          <div className="cap">Resultado IA</div>
          {loading ? (
            <Empty>
              <Spinner $size={26} />
              Gerando... pode levar ~30–60s
            </Empty>
          ) : resultUrl ? (
            <img src={resultUrl} alt="Resultado" />
          ) : (
            <Empty>A nova imagem aparece aqui</Empty>
          )}
        </Shot>
      </Preview>

      {!resultUrl && (
        <>
          <Label style={{ marginBottom: '0.5rem', display: 'block' }}>O que fazer</Label>
          <Presets>
            {(options?.actions || []).map((a) => (
              <Preset key={a.key} $active={action === a.key} onClick={() => setAction(a.key)}>
                {a.label}
              </Preset>
            ))}
          </Presets>

          {needsStyle && (
            <Field style={{ marginBottom: '1rem' }}>
              <Label>Estilo da decoração</Label>
              <Select value={style} onChange={(e) => setStyle(e.target.value)}>
                {(options?.styles || []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Label style={{ marginBottom: '0.4rem', display: 'block' }}>Motor de IA</Label>
          <EngineRow>
            {(options?.engines || ['nano', 'openai']).map((e) => {
              const ready = options?.engines_ready ? options.engines_ready[e] !== false : true;
              return (
                <EngineBtn
                  key={e}
                  $active={engine === e}
                  onClick={() => ready && setEngine(e)}
                  disabled={!ready}
                  style={!ready ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                >
                  {ENGINE_LABELS[e]?.name || e}
                  <small>{ready ? ENGINE_LABELS[e]?.hint : 'não configurado'}</small>
                </EngineBtn>
              );
            })}
          </EngineRow>

          <Field style={{ marginBottom: '1rem' }}>
            <Label>Instrução extra (opcional)</Label>
            <Textarea
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder="Ex.: deixar a parede branca, remover o tapete..."
              style={{ minHeight: '70px' }}
            />
          </Field>

          {needsStyle && (
            <Disclaimer>
              Atenção: virtual staging adiciona móveis que não existem no imóvel. Use o aviso
              "imagem com decoração virtual / ilustrativa" no anúncio.
            </Disclaimer>
          )}
        </>
      )}

      <Actions>
        {resultUrl ? (
          <>
            <Button
              $variant="ghost"
              onClick={() => {
                setResultUrl(null);
                setToken(null);
              }}
              disabled={saving}
            >
              Gerar outra
            </Button>
            <Button $variant="outline" onClick={() => handleSave('replace')} disabled={saving}>
              {saving ? 'Salvando...' : 'Substituir original'}
            </Button>
            <Button $variant="primary" onClick={() => handleSave('new')} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar como nova'}
            </Button>
          </>
        ) : (
          <>
            <Button $variant="secondary" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button $variant="primary" onClick={handleGenerate} disabled={loading || !image}>
              {loading ? 'Gerando...' : '✨ Gerar com IA'}
            </Button>
          </>
        )}
      </Actions>
    </Modal>
  );
};

export default AIEditModal;
